import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import StatsOverview from './components/StatsOverview';
import FilterBar from './components/FilterBar';
import PersonnelCard from './components/PersonnelCard';
import AddPersonnelModal from './components/AddPersonnelModal';
import ProfileModal from './components/ProfileModal';
import EditRolesModal from './components/EditRolesModal';
import PermissionControlsModal from './components/PermissionControlsModal';
import AuditLogModal from './components/AuditLogModal';
import BulkOperationsBar from './components/BulkOperationsBar';
import SendLoginInviteModal from './components/SendLoginInviteModal';
import RoleAssignmentModal from './components/RoleAssignmentModal';
import InviteUserModal from './components/InviteUserModal';

import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { supabase } from '../../lib/supabase';


const PersonnelManagement = () => {
  const [activeTab, setActiveTab] = useState('technicians');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditRolesModal, setShowEditRolesModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [showAuditLogModal, setShowAuditLogModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [invitePerson, setInvitePerson] = useState(null);
  const [showRoleAssignModal, setShowRoleAssignModal] = useState(false);
  const [roleAssignPerson, setRoleAssignPerson] = useState(null);
  const [showInviteUserModal, setShowInviteUserModal] = useState(false);
  const [customRoles, setCustomRoles] = useState({
    technicians: [],
    managers: [],
    sales: []
  });
  const [rolesLoading, setRolesLoading] = useState(true);
  const [personnelLoading, setPersonnelLoading] = useState(true);
  const [techniciansData, setTechniciansData] = useState([]);
  const [managersData, setManagersData] = useState([]);
  const [salesData, setSalesData] = useState([]);

  // Fetch custom roles from Supabase
  useEffect(() => {
    fetchCustomRoles();
    fetchPersonnel();
  }, []);

  const fetchCustomRoles = async () => {
    try {
      setRolesLoading(true);
      const { data, error } = await supabase?.from('personnel_custom_roles')?.select('*')?.order('role_name', { ascending: true });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error fetching custom roles:', error?.message);
          throw error;
        } else {
          console.log('Error fetching custom roles:', error?.message);
          return;
        }
      }

      // Group roles by role_type
      const groupedRoles = {
        technicians: [],
        managers: [],
        sales: []
      };

      data?.forEach(role => {
        if (groupedRoles?.[role?.role_type]) {
          groupedRoles?.[role?.role_type]?.push({
            id: role?.id,
            value: role?.role_name,
            label: role?.role_name
          });
        }
      });

      setCustomRoles(groupedRoles);
    } catch (error) {
      console.error('Failed to fetch custom roles:', error);
    } finally {
      setRolesLoading(false);
    }
  };

  const fetchPersonnel = async () => {
    try {
      setPersonnelLoading(true);
      const { data, error } = await supabase
        ?.from('personnel')
        ?.select(`
          *,
          personnel_skills(skill),
          personnel_equipment(equipment)
        `)
        ?.order('name', { ascending: true });

      if (error) {
        console.error('Error fetching personnel:', error?.message);
        return;
      }

      // Fetch user profiles and org members for role badges
      const emails = data?.map(p => p?.email)?.filter(Boolean);
      let profileMap = {};
      let memberMap = {};

      if (emails?.length > 0) {
        const { data: profiles } = await supabase
          ?.from('user_profiles')
          ?.select('id, email, platform_role')
          ?.in('email', emails);

        profiles?.forEach(p => {
          profileMap[p?.email] = p;
        });

        const profileIds = profiles?.map(p => p?.id)?.filter(Boolean);
        if (profileIds?.length > 0) {
          const { data: members } = await supabase
            ?.from('org_members')
            ?.select('user_id, role')
            ?.in('user_id', profileIds)
            ?.eq('is_active', true);

          members?.forEach(m => {
            memberMap[m?.user_id] = m;
          });
        }
      }

      const transformPerson = (p) => {
        const profile = profileMap?.[p?.email];
        const member = profile ? memberMap?.[profile?.id] : null;
        return {
          id: p?.id,
          name: p?.name,
          role: p?.custom_role_name || p?.role,
          email: p?.email,
          phone: p?.phone,
          status: formatStatus(p?.status),
          location: p?.location,
          avatar: p?.avatar,
          avatarAlt: p?.avatar_alt,
          skills: p?.personnel_skills?.map(s => s?.skill) || [],
          equipment: p?.personnel_equipment?.map(e => e?.equipment) || [],
          emergencyContact: p?.emergency_contact,
          emergencyPhone: p?.emergency_phone,
          platformRole: profile?.platform_role || null,
          orgRole: member?.role || null,
          performanceMetrics: {
            jobsCompleted: p?.jobs_completed || 0,
            rating: parseFloat(p?.rating) || 0
          }
        };
      };

      const technicians = data?.filter(p => p?.role === 'technician')?.map(transformPerson);
      const managers = data?.filter(p => p?.role === 'manager')?.map(transformPerson);
      const sales = data?.filter(p => p?.role === 'salesperson')?.map(transformPerson);

      setTechniciansData(technicians);
      setManagersData(managers);
      setSalesData(sales);
    } catch (error) {
      console.error('Failed to fetch personnel:', error);
    } finally {
      setPersonnelLoading(false);
    }
  };

  // Convert database status format to display format
  const formatStatus = (dbStatus) => {
    const statusMap = {
      'available': 'Available',
      'on_job': 'On Job',
      'off_duty': 'Off Duty',
      'on_leave': 'On Leave'
    };
    return statusMap?.[dbStatus] || 'Available';
  };

  function isSchemaError(error) {
    if (!error) return false;
    
    if (error?.code && typeof error?.code === 'string') {
      const errorClass = error?.code?.substring(0, 2);
      if (errorClass === '42' || errorClass === '08') {
        return true;
      }
      if (errorClass === '23') {
        return false;
      }
    }
    
    if (error?.message) {
      const schemaErrorPatterns = [
        /relation.*does not exist/i,
        /column.*does not exist/i,
        /function.*does not exist/i,
        /syntax error/i,
        /invalid.*syntax/i,
        /type.*does not exist/i,
        /undefined.*column/i,
        /undefined.*table/i,
        /undefined.*function/i,
      ];
      
      return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
    }
    
    return false;
  };

  const getCurrentData = () => {
    switch (activeTab) {
      case 'technicians':
        return techniciansData;
      case 'managers':
        return managersData;
      case 'sales':
        return salesData;
      default:
        return [];
    }
  };

  const getFilteredData = () => {
    let data = getCurrentData();

    if (searchTerm) {
      data = data?.filter((person) =>
      person?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      person?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      person?.phone?.includes(searchTerm)
      );
    }

    if (statusFilter !== 'all') {
      data = data?.filter((person) => person?.status === statusFilter);
    }

    return data;
  };

  const getStats = () => {
    let data = getCurrentData();
    return {
      total: data?.length,
      available: data?.filter((p) => p?.status === 'Available')?.length,
      onJob: data?.filter((p) => p?.status === 'On Job')?.length,
      offDuty: data?.filter((p) => p?.status === 'Off Duty')?.length
    };
  };

  const handleViewProfile = (person) => {
    setSelectedPerson(person);
  };

  const handleCloseProfile = () => {
    setSelectedPerson(null);
  };

  const handleSaveProfile = (updatedPerson) => {
    console.log('Profile updated:', updatedPerson);
    // Refresh personnel data from database
    fetchPersonnel();
    setSelectedPerson(null);
  };

  const handleDeletePerson = (personId) => {
    // Refresh personnel data from database after deletion
    fetchPersonnel();
    setSelectedPerson(null);
  };

  const handleAddPersonnel = (newPerson) => {
    // Refresh personnel data from database
    fetchPersonnel();
    setShowAddModal(false);
  };

  const handleAddPerson = (newPerson) => {
    // Refresh personnel data from database
    fetchPersonnel();
    setShowAddModal(false);
  };

  const handleSaveRoles = async (updatedRoles) => {
    try {
      // Delete all existing roles for this role type
      const { error: deleteError } = await supabase
        ?.from('personnel_custom_roles')
        ?.delete()
        ?.eq('role_type', activeTab);

      if (deleteError) {
        console.error('Error deleting old roles:', deleteError?.message);
        alert('Failed to update roles. Please try again.');
        return;
      }

      // Insert new roles if any exist
      if (updatedRoles?.length > 0) {
        const rolesToInsert = updatedRoles?.map(role => ({
          role_type: activeTab,
          role_name: role?.value
        }));

        const { error: insertError } = await supabase
          ?.from('personnel_custom_roles')
          ?.insert(rolesToInsert);

        if (insertError) {
          console.error('Error inserting new roles:', insertError?.message);
          alert('Failed to save roles. Please try again.');
          return;
        }
      }

      // Re-fetch roles to update UI
      await fetchCustomRoles();
      setShowEditRolesModal(false);
    } catch (error) {
      console.error('Failed to save roles:', error);
      alert('An error occurred while saving roles.');
    }
  };

  const handleSelectPerson = (personId, checked) => {
    setSelectedIds(prev => {
      if (checked) {
        return [...prev, personId];
      } else {
        return prev?.filter(id => id !== personId);
      }
    });
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const allIds = getFilteredData()?.map(p => p?.id);
      setSelectedIds(allIds);
    } else {
      setSelectedIds([]);
    }
  };

  const handleBulkDelete = async () => {
    try {
      // Delete related records first
      const { error: skillsError } = await supabase
        ?.from('personnel_skills')
        ?.delete()
        ?.in('personnel_id', selectedIds);

      if (skillsError) {
        console.error('Error deleting skills:', skillsError?.message);
      }

      const { error: equipmentError } = await supabase
        ?.from('personnel_equipment')
        ?.delete()
        ?.in('personnel_id', selectedIds);

      if (equipmentError) {
        console.error('Error deleting equipment:', equipmentError?.message);
      }

      // Delete personnel records
      const { error: deleteError } = await supabase
        ?.from('personnel')
        ?.delete()
        ?.in('id', selectedIds);

      if (deleteError) {
        console.error('Error deleting personnel:', deleteError?.message);
        alert('Failed to delete personnel. Please try again.');
        return;
      }

      // Refresh data
      await fetchPersonnel();
      setSelectedIds([]);
    } catch (error) {
      console.error('Failed to bulk delete:', error);
      alert('An error occurred. Please try again.');
    }
  };

  const handleBulkStatusUpdate = async (newStatus) => {
    try {
      // Map status to database format
      const statusMap = {
        'Available': 'available',
        'On Job': 'on_job',
        'Off Duty': 'off_duty'
      };

      const { error: updateError } = await supabase
        ?.from('personnel')
        ?.update({ 
          status: statusMap?.[newStatus] || 'available',
          updated_at: new Date()?.toISOString()
        })
        ?.in('id', selectedIds);

      if (updateError) {
        console.error('Error updating status:', updateError?.message);
        alert('Failed to update status. Please try again.');
        return;
      }

      // Refresh data
      await fetchPersonnel();
      setSelectedIds([]);
    } catch (error) {
      console.error('Failed to bulk update status:', error);
      alert('An error occurred. Please try again.');
    }
  };

  const handleExport = async (format) => {
    setExportLoading(true);
    await new Promise(resolve => setTimeout(resolve, 500));

    let data = getFilteredData();
    const exportData = data?.map(person => ({
      Name: person?.name,
      Email: person?.email,
      Phone: person?.phone,
      Role: person?.role,
      Status: person?.status,
      Location: person?.location || 'N/A',
      'Emergency Contact': person?.emergencyContact || 'N/A',
      'Emergency Phone': person?.emergencyPhone || 'N/A',
      'Jobs Completed': person?.performanceMetrics?.jobsCompleted || 0,
      Rating: person?.performanceMetrics?.rating || 0
    }));

    const timestamp = new Date()?.toISOString()?.split('T')?.[0];
    const filename = `personnel_${activeTab}_${timestamp}`;

    if (format === 'csv') {
      const csv = Papa?.unparse(exportData);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${filename}.csv`;
      link?.click();
    } else if (format === 'excel') {
      const worksheet = XLSX?.utils?.json_to_sheet(exportData);
      const workbook = XLSX?.utils?.book_new();
      XLSX?.utils?.book_append_sheet(workbook, worksheet, 'Personnel');
      XLSX?.writeFile(workbook, `${filename}.xlsx`);
    }

    setExportLoading(false);
  };

  const handleCloseEditRolesModal = async (shouldRefresh) => {
    setShowEditRolesModal(false);
    if (shouldRefresh) {
      // Refresh roles from database after successful save
      await fetchCustomRoles();
    }
  };

  const handleSendInvite = (person) => {
    setInvitePerson(person);
    setShowInviteModal(true);
  };

  const handleAssignRole = (person) => {
    setRoleAssignPerson(person);
    setShowRoleAssignModal(true);
  };

  const handleInviteUser = () => {
    setShowInviteUserModal(true);
  };

  const tabs = [
  { id: 'technicians', label: 'Technicians', icon: 'Wrench', count: techniciansData?.length },
  { id: 'managers', label: 'Managers', icon: 'Briefcase', count: managersData?.length },
  { id: 'sales', label: 'Sales Personnel', icon: 'TrendingUp', count: salesData?.length }];


  const filteredData = getFilteredData();
  const stats = getStats();

  return (
    <>
      <Header />
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <div className="mb-6 md:mb-8">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground mb-2">
              Personnel Management
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              Manage technicians, managers, and sales personnel with comprehensive staff coordination
            </p>
          </div>

          {personnelLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="overflow-x-auto w-full sm:w-auto">
                  <div className="flex gap-2 min-w-max">
                    {tabs?.map((tab) =>
                    <button
                      key={tab?.id}
                      onClick={() => {
                        setActiveTab(tab?.id);
                        setSelectedIds([]);
                      }}
                      className={`flex items-center gap-2 px-4 md:px-6 py-3 rounded-lg font-medium transition-smooth whitespace-nowrap ${
                      activeTab === tab?.id ?
                      'bg-primary text-primary-foreground shadow-elevation-2' :
                      'bg-card text-foreground hover:bg-muted border border-border'}`
                      }>
                      
                        <Icon name={tab?.icon} size={20} color="currentColor" />
                        <span className="text-sm md:text-base">{tab?.label}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      activeTab === tab?.id ?
                      'bg-primary-foreground/20 text-primary-foreground' :
                      'bg-muted text-muted-foreground'}`
                      }>
                          {tab?.count}
                        </span>
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <div className="relative group">
                    <Button
                      variant="outline"
                      onClick={() => handleExport('csv')}
                      loading={exportLoading}
                      disabled={exportLoading}
                      className="whitespace-nowrap"
                    >
                      <Icon name="Download" size={18} color="var(--color-foreground)" />
                      Export
                    </Button>
                    <div className="absolute right-0 top-full mt-2 w-48 bg-card border border-border rounded-lg shadow-elevation-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                      <button
                        onClick={() => handleExport('csv')}
                        disabled={exportLoading}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-muted transition-smooth flex items-center gap-2 rounded-t-lg"
                      >
                        <Icon name="FileText" size={16} color="var(--color-foreground)" />
                        Export as CSV
                      </button>
                      <button
                        onClick={() => handleExport('excel')}
                        disabled={exportLoading}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-muted transition-smooth flex items-center gap-2 rounded-b-lg"
                      >
                        <Icon name="FileSpreadsheet" size={16} color="var(--color-foreground)" />
                        Export as Excel
                      </button>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setShowAuditLogModal(true)}
                    className="whitespace-nowrap"
                  >
                    <Icon name="FileText" size={18} color="var(--color-foreground)" />
                    Audit Log
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowPermissionsModal(true)}
                    className="whitespace-nowrap"
                  >
                    <Icon name="Shield" size={18} color="var(--color-foreground)" />
                    Permissions
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowEditRolesModal(true)}
                    className="whitespace-nowrap"
                  >
                    <Icon name="Settings" size={18} color="var(--color-foreground)" />
                    Edit Roles
                  </Button>
                </div>
              </div>

              <div className="mb-6 flex justify-start gap-3">
                <Button
                  variant="default"
                  onClick={() => setShowAddModal(true)}
                  className="whitespace-nowrap"
                >
                  <Icon name="UserPlus" size={18} color="white" />
                  Add Personnel
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowInviteUserModal(true)}
                  className="whitespace-nowrap"
                >
                  <Icon name="Mail" size={16} color="currentColor" />
                  Invite via Email
                </Button>
              </div>

              <StatsOverview stats={stats} />

              <FilterBar
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                statusFilter={statusFilter}
                onStatusChange={setStatusFilter}
                roleType={activeTab}
                selectedCount={selectedIds?.length}
                totalCount={getFilteredData()?.length}
                onSelectAll={handleSelectAll}
                allSelected={selectedIds?.length === getFilteredData()?.length && getFilteredData()?.length > 0} />
              

              {selectedIds?.length > 0 && (
                <BulkOperationsBar
                  selectedCount={selectedIds?.length}
                  onBulkDelete={handleBulkDelete}
                  onBulkStatusUpdate={handleBulkStatusUpdate}
                  onClearSelection={() => setSelectedIds([])}
                />
              )}

              {filteredData?.length > 0 ?
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                  {filteredData?.map((person) =>
                <PersonnelCard
                  key={person?.id}
                  person={person}
                  roleType={activeTab === 'technicians' ? 'technician' : activeTab === 'managers' ? 'manager' : 'sales'}
                  onViewProfile={handleViewProfile}
                  isSelected={selectedIds?.includes(person?.id)}
                  onSelect={handleSelectPerson}
                  selectionMode={selectedIds?.length > 0}
                  onSendInvite={handleSendInvite}
                  onAssignRole={handleAssignRole}
                />

                )}
                </div> :

              <div className="bg-card rounded-lg border border-border p-8 md:p-12 text-center">
                  <div className="w-16 h-16 md:w-20 md:h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="Users" size={32} color="var(--color-muted-foreground)" />
                  </div>
                  <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-2">
                    No Personnel Found
                  </h3>
                  <p className="text-sm md:text-base text-muted-foreground mb-6">
                    No personnel match your current filters. Try adjusting your search criteria.
                  </p>
                  <Button
                  variant="outline"
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                  }}>
                  
                    Clear Filters
                  </Button>
                </div>
              }
            </>
          )}
        </div>
      </div>
      {selectedPerson && (
        <ProfileModal
          person={selectedPerson}
          roleType={activeTab === 'technicians' ? 'technician' : activeTab === 'managers' ? 'manager' : 'sales'}
          onClose={handleCloseProfile}
          onSave={handleSaveProfile}
          onDelete={handleDeletePerson}
        />
      )}
      {showAddModal &&
      <AddPersonnelModal
        roleType={activeTab}
        customRoles={customRoles?.[activeTab] || []}
        onClose={() => setShowAddModal(false)}
        onAdd={handleAddPerson} />

      }
      {showEditRolesModal &&
      <EditRolesModal
        roleType={activeTab}
        onClose={handleCloseEditRolesModal} />

      }
      {showPermissionsModal &&
      <PermissionControlsModal
        onClose={() => setShowPermissionsModal(false)} />

      }
      {showAuditLogModal &&
      <AuditLogModal
        onClose={() => setShowAuditLogModal(false)} />

      }
      {showInviteModal && invitePerson && (
        <SendLoginInviteModal
          person={invitePerson}
          onClose={() => { setShowInviteModal(false); setInvitePerson(null); }}
          onSuccess={() => { setShowInviteModal(false); setInvitePerson(null); }}
        />
      )}
      {showRoleAssignModal && roleAssignPerson && (
        <RoleAssignmentModal
          person={roleAssignPerson}
          onClose={() => { setShowRoleAssignModal(false); setRoleAssignPerson(null); }}
          onSaved={() => { setShowRoleAssignModal(false); setRoleAssignPerson(null); fetchPersonnel(); }}
        />
      )}
      {showInviteUserModal && (
        <InviteUserModal
          onClose={() => setShowInviteUserModal(false)}
          onSuccess={() => setShowInviteUserModal(false)}
        />
      )}
    </>
  );

};

export default PersonnelManagement;